import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { evaluate, parse } from 'mathjs';
import { Editor, toHTML } from 'ngx-editor';
import { DOMOutputSpec, NodeSpec } from 'prosemirror-model';
import { Subject } from 'rxjs';

import { App, FormulaPreview, Project, SelectableField } from '../../models';
import { FIELD_TYPE_EMPTY_VALUES_CONSTANTS, FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS, FORMULA_ERRORS_CONSTANTS } from '../../shared/constants';
import { FieldType, NumberFormatOptions } from '../../shared/enums';
import { NumberFormattingUtil } from '../../shared/utils';
import { SiteDataDataService } from '../data-services';
import { FormulaMessages, FormulaPreviewField, FormulaPreviewParameters } from './../../models/formula-preview';

@Injectable({
    providedIn: 'root',
})

export class FormulaBuilderLogicService {
    private _previewLoaded = new Subject<boolean>();
    previewLoaded$ = this._previewLoaded.asObservable();

    warningFormulas: Set<string> = new Set<string>();
    invalidFormulas: Set<string> = new Set<string>();

    replaceableFieldNode: NodeSpec = {
        inline: true,
        group: 'inline',
        atom: true,
        selectable: true,
        attrs: {
            id: {},
            class: { default: 'khts-html-replaceable-field' },
            text: { default: '' },
            title: { default: '' },
        },
        parseDOM: [{
            tag: 'span',
            getAttrs: dom => ({
                id: (dom as HTMLElement).getAttribute('id'),
                class: (dom as HTMLElement).getAttribute('class'),
                text: (dom as HTMLElement).innerText || (dom as HTMLElement).textContent || '',
                title: (dom as HTMLElement).getAttribute('title'),
            }),
        }],
        toDOM(node): DOMOutputSpec {
            const { id, class: cls, title, text } = node.attrs;
            return ['span', { id, class: cls, title }, text];
        },
    };

    constructor(private _siteDataService: SiteDataDataService) { }

    getFormulaPreviewValue(currentApp: App, currentProject: Project, formGroup: UntypedFormGroup, availableFormulaFields: SelectableField[], message: FormulaMessages, formulaPreview: FormulaPreview): void {
        formulaPreview.isPreviewLoading = true;
        const formulaPreviewParams = this.getFormulaPreviewContent(currentApp, currentProject, formGroup, availableFormulaFields);
        this._siteDataService.getFormulaPreviewValues(formulaPreviewParams).subscribe({
            next: (retrievedFormula: FormulaPreview) => {
                if (retrievedFormula) {
                    Object.assign(formulaPreview, retrievedFormula);
                    this.buildPreviewMessage(currentApp, retrievedFormula, message);
                }
                else {
                    message.previewMessage = FORMULA_ERRORS_CONSTANTS.PREVIEW_UNAVAILABLE;
                }
                this._previewLoaded.next(true);
                formulaPreview.isPreviewLoading = false;
            },
            error: () => {
                formulaPreview.isPreviewLoading = false;
            }
        });
    }

    buildPreviewMessage(app: App, previewFormula: FormulaPreview, message: FormulaMessages): void {
        const title = `<b>Preview from ${app.aliases.site.singular} ${previewFormula.siteNo} - ${previewFormula.siteName}</b><br/>`;
        message.previewMessage = title;
    }

    getFormulaPreviewContent(currentApp: App, currentProject: Project, formGroup: UntypedFormGroup, availableFormulaFields: SelectableField[]) {
        const formulaPreview: FormulaPreviewParameters = {
            appId: currentApp.id,
            projectId: currentProject.id,
            richTextFormula: formGroup.get('richTextFormula').value,
            usesDateField: this.doesFormulaContainDateField(formGroup.get('richTextFormula').value, availableFormulaFields),
            fieldsInFormula: this.retrieveFieldsInFormula(formGroup.get('richTextFormula').value, availableFormulaFields),
        };
        return formulaPreview;
    }

    retrieveFieldsInFormula(value: string, availableFields: SelectableField[]): FormulaPreviewField[] {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        const fieldSpans = Array.from(doc.querySelectorAll('span'));
        const hashIds = fieldSpans.map(span => span.getAttribute('id'));
        const fieldsFoundInFormula = hashIds.map(hashId => {
            const field = availableFields.find(f => f.hash === hashId);
            return field ? { identifier: field.identifier, hash: field.hash } : null;
        });

        return fieldsFoundInFormula;
    }

    setAvailableFormulaFields(gridId: string, fields: SelectableField[]): SelectableField[] {
        let availableFormulaFields: SelectableField[];
        if (gridId) {
            availableFormulaFields = fields.filter(f => f.identifier.fieldId === gridId && ((f.fieldTypeId === FieldType.Number && f.format !== NumberFormatOptions.Hours) || f.fieldTypeId === FieldType.DateSelect));
        }
        else {
            availableFormulaFields = fields.filter(f => (f.identifier.gridColumnId === undefined) && ((f.fieldTypeId === FieldType.Number && f.format !== NumberFormatOptions.Hours) || f.fieldTypeId === FieldType.DateSelect));
        }
        return availableFormulaFields;
    }

    onFormulaChange(newValue: Record<string, any>, formGroup: UntypedFormGroup, formulaTemplate: Record<string, any>, formulaTemplateEditor: Editor, messages: FormulaMessages, availableFormulaFields: SelectableField[], publishedFormat: string): void {
        formGroup.get('richTextFormula').markAsTouched();
        formGroup.get('richTextFormula').markAsDirty();
        formulaTemplate = newValue;
        let formulaValue = toHTML(formulaTemplate, formulaTemplateEditor.schema);
        if (formulaValue === FIELD_TYPE_EMPTY_VALUES_CONSTANTS.TextAreaEmptyParagraphTag) {
            formGroup.get('format').setValue(FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.NUMBER);
            formulaValue = undefined;
            formGroup.get('richTextFormula').setValue(formulaValue);
            return;
        }

        formGroup.get('richTextFormula').setValue(formulaValue);

        messages.errorMessage = '';
        messages.dateErrorMessage = '';

        this.validateAllFieldsInFormulaAreValid(formGroup, availableFormulaFields, messages);

        if (this.doesFormulaContainDateField(formGroup.get('richTextFormula').value, availableFormulaFields)) {
            if (publishedFormat && (publishedFormat !== FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE && publishedFormat !== NumberFormatOptions.Duration)) {
                formGroup.get('richTextFormula').setErrors({ 'formulaError': true });
                messages.errorMessage = FORMULA_ERRORS_CONSTANTS.CHANGE_FROM_NUMBER;
                return;
            }
            this.validateAndSetDateErrorsAndFormat(formGroup, messages, availableFormulaFields);
        }
        else {
            if (publishedFormat && (publishedFormat === FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE) && availableFormulaFields.length > 0) {
                formGroup.get('richTextFormula').setErrors({ 'formulaError': true });
                messages.errorMessage = FORMULA_ERRORS_CONSTANTS.CHANGE_FROM_DATE;
                return;
            }
            this.validateAndSetNumberErrors(formGroup, messages);
        }

        if (messages.errorMessage || messages.dateErrorMessage) {
            return;
        }
        this.evaluatePreviewFormulaAndCheckErrors(formGroup, messages);
    }

    validateAllFieldsInFormulaAreValid(formGroup: UntypedFormGroup, availableFormulaFields: SelectableField[], messages: FormulaMessages): string[] {
        if (availableFormulaFields.length === 0) {
            return [];
        }
        messages.deletedFieldReferencedMessage = '';
        const parser = new DOMParser();
        const doc = parser.parseFromString(formGroup.get('richTextFormula').value, 'text/html');

        const fieldSpans = Array.from(doc.querySelectorAll('span'));
        const ids = fieldSpans.map(span => span.getAttribute('id'));
        let missingIds: string[] = [];
        if (ids.some(id => !availableFormulaFields.some(f => f.hash === id))) {
            missingIds = ids.filter(id =>
                !availableFormulaFields.some(f => f.hash === id)
            );
            messages.deletedFieldReferencedMessage = FORMULA_ERRORS_CONSTANTS.DELETED_FIELD_REFERENCED;
        }
        return missingIds;
    }

    addClassesToMissingFieldsInTemplate(editor: Editor, missingIds: string[]): void {
        const view = editor.view;
        if (!view) {
            return;
        }

        const state = view.state;
        const tr = state.tr;
        let modified = false;

        state.doc.descendants((node, pos) => {
            if (node.type.name !== "replaceableField") {
                return;
            }

            const id = node.attrs?.id;
            if (!id) {
                return;
            }

            const current = node.attrs.class ?? "";
            const deletedClass = "khts-html-replaceable-field--deleted";
            const hasDeleted = current.includes(deletedClass);

            let newClass = current;

            if (missingIds.includes(id)) {
                if (!hasDeleted) {
                    newClass = current
                        ? `${current} ${deletedClass}`
                        : deletedClass;
                }
                else {
                    return;
                }
            }
            else {
                if (hasDeleted) {
                    newClass = current
                        .split(" ")
                        .filter(c => c !== deletedClass)
                        .join(" ");
                }
                else {
                    return;
                }
            }

            tr.setNodeMarkup(pos, null, {
                ...node.attrs,
                class: newClass
            });

            modified = true;
        });

        if (modified) {
            view.dispatch(tr);
        }
    }

    doesFormulaContainDateField(value: string, availableFields: SelectableField[]): boolean {
        return this.dateFieldIdsInFormula(value, availableFields).length > 0;
    }

    doesFormulaContainOnlyDateFields(value: string, availableFields: SelectableField[]): boolean {
        return this.dateFieldIdsInFormula(value, availableFields).length > 1;
    }

    private dateFieldIdsInFormula(value: string, availableFields: SelectableField[]): string[] {
        return this.retrieveAllFieldsInRichTextFormula(value)
            .filter((id: string) => {
                const matched = availableFields.find((field: SelectableField) => field.hash === id);
                return matched?.fieldTypeId === FieldType.DateSelect;
            });
    }

    validateAndSetDateErrorsAndFormat(formGroup: UntypedFormGroup, messages: FormulaMessages, availableFields: SelectableField[]): void {
        messages.dateErrorMessage = this.validateDateFormula(formGroup, availableFields);
        if (messages.dateErrorMessage) {
            formGroup.get('richTextFormula').setErrors({ 'dateError': true });
        }
        else {
            formGroup.get('richTextFormula').setErrors(undefined);
        }
        return;
    }

    validateDateFormula(formGroup: UntypedFormGroup, availableFields: SelectableField[]): string {
        try {
            const operatorError = this.validateDateOperators(formGroup.get('richTextFormula').value);
            if (operatorError) {
                return operatorError;
            }

            const fieldOrderError = this.validateDateFieldOrderAndSetFormat(formGroup, availableFields);
            if (fieldOrderError) {
                return fieldOrderError;
            }

            const nodeSymbolError = this.validateDateSymbols(formGroup.get('richTextFormula').value);
            if (nodeSymbolError) {
                return nodeSymbolError;
            }
            return '';
        }
        catch (e) {
            return FORMULA_ERRORS_CONSTANTS.INVALID_FORMULA;
        }
    }

    validateDateOperators(value: string) {
        const nodeToCheckOperators = parse(value.replace(/<span [^>]*>(.*?)<\/span>/g, '(1)').replace('<p>', '').replace('</p>', ''));
        let areOperatorsValid = true;

        nodeToCheckOperators.traverse((childNode) => {
            if (childNode.type === 'OperatorNode') {
                const op = (childNode as any).op;
                if (op !== '+' && op !== '-') {
                    areOperatorsValid = false;
                }
            }
        });
        if (!areOperatorsValid) {
            return FORMULA_ERRORS_CONSTANTS.DATE;
        }
    }

    validateDateFieldOrderAndSetFormat(formGroup: UntypedFormGroup, availableFields: SelectableField[]) {
        const value = formGroup.get('richTextFormula').value;
        const nodeToCheckOrder = parse(value.replace(/<span [^>]*>(.*?)<\/span>/g, 'FIELD').replace('<p>', '').replace('</p>', ''));
        let isOrderValid = true;
        let shouldSetFormat = false;

        nodeToCheckOrder.traverse((childNode) => {
            if (childNode.type === 'OperatorNode') {
                const { op, args } = childNode as any;
                if (args.length !== 2) {
                    isOrderValid = false;
                    return;
                }

                if (args[0].name !== 'FIELD') {
                    isOrderValid = false;
                }

                if (args[1].name == 'FIELD' && op === '+') {
                    isOrderValid = false;
                }

                shouldSetFormat = true;
            }
        });

        if (shouldSetFormat) {
            formGroup.get('format').setValue(this.doesFormulaContainOnlyDateFields(value, availableFields)
                ? NumberFormatOptions.Duration
                : FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE);
        }

        if (!isOrderValid) {
            return FORMULA_ERRORS_CONSTANTS.DATE;
        }
    }

    validateDateSymbols(value: string) {
        const node = parse(value.replace(/<span [^>]*>(.*?)<\/span>/g, '(1)').replace('<p>', '').replace('</p>', ''));
        return !this.formulaContainsSymbols(node) ? '' : FORMULA_ERRORS_CONSTANTS.DATE;
    }

    validateAndSetNumberErrors(formGroup: UntypedFormGroup, messages: { errorMessage: string, dateErrorMessage: string; }): void {
        if (formGroup.get('format').value === FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.DATE) {
            formGroup.get('format').setValue(FORM_BUILDER_FORMAT_OPTIONS_CONSTANTS.NUMBER);
        }

        messages.errorMessage = this.validateFormula(formGroup.get('richTextFormula').value);

        if (messages.errorMessage) {
            formGroup.get('richTextFormula').setErrors({ 'formulaError': true });
        }
        else {
            formGroup.get('richTextFormula').setErrors(undefined);
        }
    }

    validateFormula(value: string): string {
        try {
            const node = parse(value.replace(/<span [^>]*>(.*?)<\/span>/g, '(1)').replace('<p>', '').replace('</p>', ''));
            let nodeContainsInvalidSymbol = false;
            nodeContainsInvalidSymbol = this.formulaContainsSymbols(node);

            if (nodeContainsInvalidSymbol) {
                return FORMULA_ERRORS_CONSTANTS.INVALID_FORMULA;
            }
            return '';
        }
        catch (e) {
            return FORMULA_ERRORS_CONSTANTS.INVALID_FORMULA;
        }
    }

    formulaContainsSymbols(node): boolean {
        if (node.type === 'SymbolNode') {
            return true;
        }

        if (node.args && Array.isArray(node.args)) {
            for (const child of node.args) {
                if (this.formulaContainsSymbols(child)) {
                    return true;
                }
            }
        }

        return false;
    }

    doesFormulaContainFields(value: string): boolean {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        const fieldSpans = Array.from(doc.querySelectorAll('span'));
        return fieldSpans.length > 0;
    }

    evaluatePreviewFormulaAndCheckErrors(formGroup: UntypedFormGroup, message: FormulaMessages): void {
        try {
            evaluate(formGroup.get('richTextFormula').value.replace(/<span [^>]*>(.*?)<\/span>/g, '(1)').replace('<p>', '').replace('</p>', ''));
        }
        catch (e) {
            formGroup.get('richTextFormula').setErrors({ 'formulaError': true });
            message.errorMessage = FORMULA_ERRORS_CONSTANTS.INVALID_FORMULA;
        }
    }

    evaluatePreviewFormula(value: string): number {
        return evaluate(value.replace('<p>', '').replace('</p>', ''));
    }

    formattedFormulaValue(formGroup: UntypedFormGroup): string {
        const rawValue = this.evaluatePreviewFormula(formGroup.get('richTextFormula').value);
        return this.formatResult(formGroup, rawValue);
    }

    formatResult(formGroup: UntypedFormGroup, rawValue: number | string) {
        const format = formGroup.get('format')?.value;
        const decimalPlaces = formGroup.get('decimalPlaces')?.value;

        let formattedValue: string;
        if (NumberFormattingUtil.isMeasureofTime(format)) {
            formattedValue = NumberFormattingUtil.formatNumberToDuration(rawValue);
        }
        else {
            formattedValue = NumberFormattingUtil.formatNumberDecimalPlaces(rawValue, decimalPlaces, format);
        }

        const prefix = NumberFormattingUtil.getPrefix(format);
        const suffix = NumberFormattingUtil.getSuffix(format, formattedValue);

        return `${prefix}${formattedValue}${suffix}`;
    }

    addToInvalidFormulasSet(formulaFieldId: string): void {
        this.invalidFormulas.add(formulaFieldId);
    }

    removeFromInvalidFormulasSet(formulaFieldId: string): void {
        this.invalidFormulas.delete(formulaFieldId);
    }

    addToWarningFormulasSet(formulaFieldId: string): void {
        this.warningFormulas.add(formulaFieldId);
    }

    removeFromWarningFormulasSet(formulaFieldId: string): void {
        this.warningFormulas.delete(formulaFieldId);
    }

    clearInvalidAndWarningFormulasSets() {
        this.invalidFormulas.clear();
        this.warningFormulas.clear();
    }

    validateFormulaInColumn(form: UntypedFormGroup, fieldsAvailableForFormulas: SelectableField[]) {
        const allFieldsHashInFormula = this.retrieveAllFieldsInRichTextFormula(form.get('richTextFormula')?.value);
        const missingFieldsInFormula = allFieldsHashInFormula.filter(fieldHash => !fieldsAvailableForFormulas.some(f => f.hash === fieldHash));
        if (missingFieldsInFormula.length > 0) {
            this.addToWarningFormulasSet(form.get('id').value);
        }
    }

    retrieveAllFieldsInRichTextFormula(value: string): string[] {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        const fieldSpans = Array.from(doc.querySelectorAll('span'));
        const hashIds = fieldSpans.map(span => span.getAttribute('id'));
        return hashIds;
    }
}
